import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import {
  listPaymentOptions as clientListPaymentOptions,
  selectPaymentOption as clientSelectPaymentOption,
} from 'typescript/clients/solidshop/payment_options_client';
import { QUERY_KEYS, MUTATION_KEYS } from 'typescript/constants/react_query';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { PaymentScheduleNoInstallment } from 'typescript/types/api/payment_options';

const useListPaymentOptions = ({
  orderNumber,
  countryCode,
  languageCode,
}: {
  orderNumber: string;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();

  const { data: paymentOptionsData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.PAYMENT_OPTIONS, { orderNumber }],
    queryFn: () =>
      clientListPaymentOptions({
        orderNumber,
        countryCode: countryCode ?? contextCountryCode,
        languageCode: languageCode ?? contextLanguageCode,
      }),
  });

  return { paymentOptionsData, ...rest };
};

const useSelectPaymentOption = ({ orderNumber }: { orderNumber: string }) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();
  const queryClient = useQueryClient();

  const { mutate: selectPaymentOption, ...rest } = useMutation({
    mutationKey: [MUTATION_KEYS.SELECT_PAYMENT_OPTION],
    mutationFn: ({
      paymentOption,
      countryCode = contextCountryCode,
      languageCode = contextLanguageCode,
    }: {
      paymentOption: PaymentScheduleNoInstallment;
      countryCode?: CountryCode;
      languageCode?: LanguageCode;
    }) =>
      clientSelectPaymentOption({
        languageCode,
        orderNumber,
        paymentOption,
        countryCode,
      }),
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYMENT_OPTIONS, { orderNumber }],
      });
    },
    onError: (error) =>
      captureException(
        new CustomError(MUTATION_KEYS.SELECT_PAYMENT_OPTION, error),
        {
          extra: {
            orderNumber,
          },
        },
      ),
  });

  return { selectPaymentOption, ...rest };
};

export { useListPaymentOptions, useSelectPaymentOption };
